import React from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Maximize2, Pause, Play, X } from "lucide-react";
import { DELIVERY_COLOR, LOADING_COLOR } from "./consolidation/palette.js";
import { HoverCard } from "./resultsPrimitives/hoverCardPrimitives.jsx";
import { createCompartmentTruck, createDumpTruck } from "./resultsPageChrome/menuHelpTruck.js";
import { getFleetProductTone } from "../utils/cropColors.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import {
  buildTruckLoadTimeline,
  findTruckLoadEntries,
  summarizeTruckLoad,
  truckLoadBlockOfEntry,
  truckLoadLastEntry,
  truckLoadMarkedState,
  truckLoadMinutesAtPercent,
  truckLoadOperationPart,
  truckLoadTimeAtPercent,
  truckLoadTimelineAt,
} from "../utils/truckLoadTimeline.js";
import "./TruckLoadView.css";

// Camion d'un segment du Gantt. L'infobulle montre son contenu après le segment (TruckLoadPreview) ;
// « Agrandir » ouvre la vue de la journée avec sa ligne du temps (TruckLoadView, montée une fois dans le Dispatch).
// La vue vit hors de l'infobulle : elle reste ouverte quand l'infobulle se ferme.

export const SHOW_TRUCK_LOAD_VIEW = "dispatch:show-truck-load-view";

const formatTons = (value) => `${(Math.round(value * 10) / 10).toLocaleString("fr-CA", { maximumFractionDigits: 1 })} t`;
const formatClock = (minutes) => `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(Math.round(minutes % 60)).padStart(2, "0")}`;
const CHIP_CLASS_NAME = "inline-flex items-center gap-1 whitespace-nowrap rounded-[4px] px-1.5 py-px text-[0.62rem] font-bold leading-4 tabular-nums";
// Camion de l'infobulle : bas (cadré du haut des noms au bas des roues, sans la place de la chute)
// et remorque allongée pour occuper la largeur.
const PREVIEW_STRETCH = 260;
const PREVIEW_VIEWBOX = `8 -8 ${1252 + PREVIEW_STRETCH} 370`;
// Dompeur : un peu plus large, pour le tas de grain derrière la benne levée d'une livraison.
const DUMP_PREVIEW_VIEWBOX = `8 -8 ${1340 + PREVIEW_STRETCH} 370`;
const SECONDARY_BUTTON_CLASS_NAME = "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-[4px] border border-slate-300 bg-white font-bold text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
const LANE_TONES = {
  load: { background: "var(--dispatch-segment-loading-fill, #9bb2d0)", borderColor: "var(--dispatch-segment-loading-border, #83a0c5)", color: "var(--dispatch-segment-loading-ink, #1c3048)" },
  deliv: { background: "var(--dispatch-segment-delivery-fill, #d3c195)", borderColor: "var(--dispatch-segment-delivery-border, #b79b53)", color: "var(--dispatch-segment-delivery-ink, #373018)" },
  veille: { background: "#f2cf55", borderColor: "#c9a227", color: "#3b2f00" },
};

function truckColors(timeline) {
  const colors = {};
  timeline.entries.forEach((entry) => entry.orderIds.forEach((order) => {
    colors[order] = getFleetProductTone(timeline.productByOrder.get(order) || "").accentColor || "#64748b";
  }));
  return colors;
}

// Les positions du camion dessiné vont de 1 à N, dans l'ordre des compartiments.
// kind, unload et orders servent au dompeur : benne levée pendant et après une livraison.
function toDrawState(timeline, state) {
  const indexOf = (pos) => timeline.compartments.findIndex((compartment) => compartment.pos === Number(pos)) + 1;
  const fills = {};
  Object.entries(state.fills || {}).forEach(([pos, layers]) => {
    const index = indexOf(pos);
    const capacity = timeline.compartments[index - 1]?.cap;
    if (index && capacity) fills[index] = layers.map((layer) => ({ order: layer.order, f: layer.tons / capacity, label: "" }));
  });
  return {
    fills,
    lids: Object.fromEntries(Object.entries(state.lids || {}).map(([pos, value]) => [indexOf(pos), value])),
    hot: (state.hot || []).map(indexOf),
    spout: state.spout ? { ...state.spout, pos: indexOf(state.spout.pos), fromPos: indexOf(state.spout.fromPos) } : null,
    drain: state.drain ? { ...state.drain, pos: indexOf(state.drain.pos) } : null,
    kind: state.kind,
    unload: state.unload || null,
    orders: state.orders || [],
  };
}

// Zones de survol posées sur les compartiments du dessin : chacune ouvre l'infobulle du compartiment.
// elevated : au-dessus de la vue agrandie, qui couvre les infobulles ordinaires.
function CompartmentHoverZones({ timeline, fills = {}, zones = [], elevated = false }) {
  return zones.map((zone, index) => {
    const compartment = timeline.compartments[index];
    if (!compartment) return null;
    const layers = fills[compartment.pos] || [];
    const tons = layers.reduce((sum, layer) => sum + layer.tons, 0);
    const products = layers.map((layer) => timeline.productByOrder.get(layer.order) || "");
    return (
      <HoverCard
        key={compartment.pos}
        title={timeline.dump ? "Benne" : `Compartiment C${compartment.pos}`}
        content={(
          <div className="grid gap-0.5 text-[0.68rem] leading-4 text-content-copy tabular-nums">
            {layers.length ? layers.map((layer, layerIndex) => (
              <span key={layer.order} className="flex items-center gap-1.5">
                <i className="h-2.5 w-2.5 shrink-0 rounded-none" style={{ background: getFleetProductTone(products[layerIndex]).accentColor }} />
                <b className="text-content-ink">{layer.order}</b>{products[layerIndex]} · {formatTons(layer.tons)}
              </span>
            )) : <span>Vide</span>}
            <span className="text-content-muted">
              {layers.length ? `${formatTons(tons)} / ${formatTons(compartment.cap)} · ${Math.round((tons / compartment.cap) * 100)} %` : `Capacité ${formatTons(compartment.cap)}`}
            </span>
          </div>
        )}
        accentColor={layers.length ? getFleetProductTone(products[0]).accentColor : "#94a3b8"}
        placement="top"
        delay={80}
        elevated={elevated}
        referenceAsChild
        flat
        compactTitle
        tight
      >
        <span
          className="absolute"
          style={{ left: `${zone.left}%`, top: `${zone.top}%`, width: `${zone.width}%`, height: `${zone.height}%` }}
          data-truck-load-compartment={compartment.pos}
        />
      </HoverCard>
    );
  });
}

// Camion créé une fois par journée de camion, redessiné à chaque état. Retourne le centre de chaque
// compartiment et sa zone (en % du dessin affiché) pour les étiquettes et les infobulles.
// Un dompeur est dessiné avec sa benne ; label la nomme au-dessus du dessin.
function useCompartmentTruck(hostRef, timeline, state, { stretch = 0, viewBox = "", label = "" } = {}) {
  const truckRef = React.useRef(null);
  const [layout, setLayout] = React.useState({ percents: [], zones: [] });
  React.useEffect(() => {
    if (!hostRef.current || !timeline) return undefined;
    const createTruck = timeline.dump ? createDumpTruck : createCompartmentTruck;
    const truck = createTruck(hostRef.current, { capacities: timeline.compartments.map((compartment) => compartment.cap), colors: truckColors(timeline), stretch, label });
    truckRef.current = truck;
    if (viewBox) truck.svg.setAttribute("viewBox", viewBox);
    const box = truck.svg.viewBox.baseVal;
    setLayout({
      percents: timeline.compartments.map((_, index) => truck.percentOf(index + 1)),
      zones: truck.comps.map((comp) => ({
        left: ((comp.x0 - box.x) / box.width) * 100,
        width: ((comp.x1 - comp.x0) / box.width) * 100,
        top: ((comp.poly[0][1] - box.y) / box.height) * 100,
        height: ((comp.poly[3][1] - comp.poly[0][1]) / box.height) * 100,
      })),
    });
    return () => { truck.destroy(); truckRef.current = null; };
  }, [hostRef, label, stretch, timeline, viewBox]);
  React.useEffect(() => {
    if (truckRef.current && state) truckRef.current.draw(toDrawState(timeline, state));
  }, [layout, state, timeline]);
  return layout;
}

// Segments survolés → étapes du camion. null quand le camion ne peut pas être dessiné (on garde les carrés).
export function useTruckLoadMatch(assignment, segments) {
  return React.useMemo(() => {
    const timeline = buildTruckLoadTimeline(assignment);
    const entries = findTruckLoadEntries(timeline, segments);
    return entries.length ? { timeline, entries } : null;
  }, [assignment, segments]);
}

export function TruckLoadPreview({ match, truckLabel = "", trailing = null }) {
  const hostRef = React.useRef(null);
  const { timeline, entries } = match;
  const state = React.useMemo(() => truckLoadMarkedState(timeline, entries), [entries, timeline]);
  const { zones } = useCompartmentTruck(hostRef, timeline, state, timeline.dump
    ? { stretch: PREVIEW_STRETCH, viewBox: DUMP_PREVIEW_VIEWBOX, label: "Benne" }
    : { stretch: PREVIEW_STRETCH, viewBox: PREVIEW_VIEWBOX });
  const summary = summarizeTruckLoad(timeline, state.fills);
  const capacity = formatTons(timeline.capacityTons);
  // Pastille qui explique les cadres : compartiments du segment (nommés s'ils sont peu nombreux) et tonnage.
  const hotNames = timeline.dump ? [] : [...state.hot].sort((left, right) => left - right).map((pos) => `C${pos}`);
  const segmentTons = entries.reduce((sum, index) => sum + (timeline.entries[index].tons || 0), 0);
  const segmentChip = (
    <span
      key="segment"
      className={`${CHIP_CLASS_NAME} ${state.kind === "load" ? "text-white" : "text-[#1f1606]"}`}
      style={{ background: state.kind === "load" ? LOADING_COLOR : DELIVERY_COLOR }}
    >
      {state.kind === "load" ? "Chargé ici" : "Livré ici"} : {hotNames.length && hotNames.length <= 3 ? `${hotNames.join(", ")} · ` : ""}{formatTons(segmentTons)}
    </span>
  );
  const chips = state.kind === "load"
    ? [
        segmentChip,
        <span key="after" className={`${CHIP_CLASS_NAME} bg-accent text-white`}>Après : {formatTons(summary.tons)} / {capacity}</span>,
        summary.free.length ? (
          <span key="free" className={`${CHIP_CLASS_NAME} bg-[#ffc400] text-[#3b2f00]`}>
            {summary.free.length > 1 ? "Libres" : "Libre"} : {summary.free.map(({ pos }) => `C${pos}`).join(", ")}
          </span>
        ) : null,
      ]
    : [
        segmentChip,
        <span key="left" className={`${CHIP_CLASS_NAME} bg-accent text-white`}>
          {summary.tons > 0 ? `Reste : ${formatTons(summary.tons)} / ${capacity}` : "Camion vide"}
        </span>,
      ];
  const openView = (event) => {
    event.stopPropagation();
    window.dispatchEvent(new CustomEvent(SHOW_TRUCK_LOAD_VIEW, {
      detail: { key: Date.now(), timeline, entryIndex: truckLoadLastEntry(timeline, entries), truckLabel },
    }));
  };

  return (
    <div className="grid min-w-0 gap-1" data-truck-load-preview>
      <div className="flex min-w-0 items-start gap-2">
        <div className="relative mx-auto min-w-0">
          <div ref={hostRef} className="truck-load-truck truck-load-preview" data-kind={state.kind} />
          <CompartmentHoverZones timeline={timeline} fills={state.fills} zones={zones} />
        </div>
        {trailing ? <div className="shrink-0">{trailing}</div> : null}
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        {chips}
        <button type="button" className={`${SECONDARY_BUTTON_CLASS_NAME} ml-auto h-5.5 px-1.5 text-[0.62rem]`} onClick={openView} title="Ligne du temps de la journée du camion" data-truck-load-open>
          <Maximize2 className="h-3 w-3" aria-hidden="true" />
          Agrandir
        </button>
      </div>
    </div>
  );
}

export default function TruckLoadView() {
  const [view, setView] = React.useState(null);
  React.useEffect(() => {
    const show = (event) => setView(event.detail?.timeline ? event.detail : null);
    window.addEventListener(SHOW_TRUCK_LOAD_VIEW, show);
    return () => window.removeEventListener(SHOW_TRUCK_LOAD_VIEW, show);
  }, []);
  const portalRoot = getReactPortalRoot();
  if (!view || !portalRoot) return null;
  const { key, ...panelProps } = view;
  return createPortal(<TruckLoadPanel key={key} {...panelProps} onClose={() => setView(null)} />, portalRoot);
}

function TruckLoadPanel({ timeline, entryIndex, truckLabel, onClose }) {
  const panelRef = React.useRef(null);
  const hostRef = React.useRef(null);
  const laneRef = React.useRef(null);
  const openingPart = truckLoadOperationPart(timeline, truckLoadBlockOfEntry(timeline, entryIndex));
  const reducedMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  // À l'ouverture, on joue le segment survolé puis on s'arrête sur son bilan.
  const [time, setTime] = React.useState(() => (reducedMotion ? openingPart?.t1 : openingPart?.t0) ?? 0);
  const [playing, setPlaying] = React.useState(!reducedMotion && Boolean(openingPart));
  const [position, setPosition] = React.useState(null);
  const timeRef = React.useRef(time);
  const stopAtRef = React.useRef(openingPart?.t1 ?? null);
  const frame = React.useMemo(() => truckLoadTimelineAt(timeline, time), [time, timeline]);
  const { percents, zones } = useCompartmentTruck(hostRef, timeline, frame.state);
  const goTo = React.useCallback((nextTime) => { timeRef.current = nextTime; setTime(nextTime); }, []);

  React.useEffect(() => {
    if (!playing) return undefined;
    let last = performance.now();
    let raf = requestAnimationFrame(function tick(now) {
      const stopAt = stopAtRef.current ?? timeline.duration;
      const next = Math.min(stopAt, timeRef.current + (now - last) / 1000);
      last = now;
      goTo(next);
      if (next >= stopAt) setPlaying(false); else raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [goTo, playing, timeline]);

  const operationParts = timeline.parts.filter((part) => part.kind === "op");
  const togglePlay = React.useCallback(() => {
    if (playing) { setPlaying(false); return; }
    if (timeRef.current >= timeline.duration - 0.01) goTo(0);
    stopAtRef.current = null;
    setPlaying(true);
  }, [goTo, playing, timeline]);
  const playNextStop = React.useCallback(() => {
    const part = operationParts.find((candidate) => candidate.t1 > timeRef.current + 0.01);
    if (!part) return;
    goTo(Math.max(timeRef.current, part.t0));
    stopAtRef.current = part.t1;
    setPlaying(true);
  }, [goTo, operationParts]);
  const backToPreviousStop = React.useCallback(() => {
    setPlaying(false);
    const ends = operationParts.filter((part) => part.t1 < timeRef.current - 0.01);
    goTo(ends.length ? ends[ends.length - 1].t1 : 0);
  }, [goTo, operationParts]);
  const seek = (event) => {
    const rect = laneRef.current.getBoundingClientRect();
    setPlaying(false);
    goTo(truckLoadTimeAtPercent(timeline, ((event.clientX - rect.left) / rect.width) * 100));
  };

  // Centrer la fenêtre dans la partie visible du Gantt, indépendamment du segment ouvert.
  React.useLayoutEffect(() => {
    const gantt = document.querySelector('[data-fleet-timeline-panel="true"]');
    const updatePosition = () => {
      const rect = gantt?.getBoundingClientRect();
      const left = Math.max(0, rect?.left ?? 0);
      const right = Math.min(window.innerWidth, rect?.right ?? window.innerWidth);
      const top = Math.max(0, rect?.top ?? 0);
      const bottom = Math.min(window.innerHeight, rect?.bottom ?? window.innerHeight);
      setPosition({
        left: (left + right) / 2,
        top: (top + bottom) / 2,
        width: Math.min(880, Math.max(0, right - left - 24)),
        maxHeight: Math.max(0, bottom - top - 24),
      });
    };
    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    if (gantt) observer.observe(gantt);
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
    };
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.target?.closest?.("input, textarea, select, [contenteditable='true']")) return;
      const action = { Escape: onClose, " ": togglePlay, ArrowRight: playNextStop, ArrowLeft: backToPreviousStop }[event.key];
      if (!action) return;
      event.preventDefault();
      event.stopPropagation();
      action();
    };
    const handlePointerDown = (event) => { if (!panelRef.current?.contains(event.target)) onClose(); };
    window.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [backToPreviousStop, onClose, playNextStop, togglePlay]);

  const block = frame.block >= 0 ? timeline.blocks[frame.block] : null;
  const nextBlock = frame.nextBlock >= 0 ? timeline.blocks[frame.nextBlock] : null;
  const atStop = block && (frame.inOperation || frame.ended);
  const stopLabel = atStop
    ? `${frame.inOperation ? "▶ " : ""}${block.label} · ${block.kind === "load" ? "chargement" : "livraison"} ${block.orderIds.join(" + ")} · ${formatTons(block.tons)}`
    : nextBlock ? `${block ? "En route vers" : "Avant"} ${nextBlock.label}` : "Fin de la journée";
  const summary = summarizeTruckLoad(timeline, frame.state.fills);
  const minutes = truckLoadMinutesAtPercent(timeline, frame.x);
  const products = [...new Set([...timeline.productByOrder.values()])];
  const subject = timeline.dump ? "Benne" : "Compartiments";
  const dumpShare = Math.round((summary.tons / timeline.capacityTons) * 100);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={`${subject} ${truckLabel}`}
      className="fixed z-[10000] grid -translate-x-1/2 -translate-y-1/2 gap-2 overflow-y-auto rounded-[4px] border border-slate-300 border-t-[3px] border-t-accent bg-white px-3.5 pb-3 pt-2.5 text-slate-700 shadow-[0_18px_44px_rgba(15,23,42,0.28)]"
      style={position ?? { visibility: "hidden" }}
      data-truck-load-view
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <b className="shrink-0 text-[0.95rem] text-slate-900">{truckLabel ? `${truckLabel} · ` : ""}{subject}</b>
        <span className="min-w-0 truncate text-[0.8rem] font-semibold text-slate-600" data-truck-load-stop>{stopLabel}</span>
        <button type="button" className={`${SECONDARY_BUTTON_CLASS_NAME} ml-auto h-7 w-7`} onClick={onClose} title="Fermer (Échap)" aria-label="Fermer">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_128px] items-end gap-3.5">
        <div className="min-w-0">
          <div className="relative">
            <div ref={hostRef} className="truck-load-truck" data-kind={frame.ended ? frame.state.kind : "play"} />
            <CompartmentHoverZones timeline={timeline} fills={frame.state.fills} zones={zones} elevated />
          </div>
          <div className="relative h-7.5">
            {timeline.compartments.map((compartment, index) => {
              const tons = (frame.state.fills[compartment.pos] || []).reduce((sum, layer) => sum + layer.tons, 0);
              const percent = Math.round((tons / compartment.cap) * 100);
              return (
                <span key={compartment.pos} className="absolute top-0 -translate-x-1/2 text-center text-[0.62rem] leading-tight tabular-nums whitespace-nowrap" style={{ left: `${percents[index] ?? 0}%` }}>
                  <b className={`block text-[0.68rem] ${tons > 0.04 ? "text-slate-900" : "text-slate-400"}`}>{tons > 0.04 ? formatTons(tons) : "—"}</b>
                  <span className={tons > 0.04 && percent < 98 ? "font-extrabold text-[#a86a24]" : "text-slate-500"}>{tons > 0.04 ? `${percent} %` : formatTons(compartment.cap)}</span>
                </span>
              );
            })}
          </div>
        </div>
        <div className="grid gap-1.5 pb-8">
          <span className="text-[0.7rem] font-bold text-slate-600">Poids à bord</span>
          <span className="text-[1.2rem] font-extrabold tabular-nums text-slate-900">
            {formatTons(summary.tons)} <small className="text-[0.75rem] font-semibold text-slate-500">/ {formatTons(timeline.capacityTons)}</small>
          </span>
          <span className="relative h-2.5 overflow-hidden rounded-[4px] bg-slate-200">
            <b className="absolute inset-y-0 left-0 rounded-[4px] bg-accent" style={{ width: `${Math.min(100, (summary.tons / timeline.capacityTons) * 100)}%` }} />
          </span>
          <span className="text-[0.7rem] font-bold text-slate-600">
            {timeline.dump
              ? (summary.tons > 0 ? `Benne : ${dumpShare} %` : "Benne vide")
              : `${summary.used.length} / ${timeline.compartments.length} compartiments`}
          </span>
        </div>
      </div>

      <div className="relative mt-5 select-none">
        <div
          ref={laneRef}
          className="relative h-7.5 cursor-ew-resize touch-none rounded-[4px] border border-slate-200 bg-slate-50"
          onPointerDown={(event) => { event.currentTarget.setPointerCapture(event.pointerId); seek(event); }}
          onPointerMove={(event) => { if (event.currentTarget.hasPointerCapture(event.pointerId)) seek(event); }}
          data-truck-load-lane
        >
          {timeline.blocks.map((laneBlock, index) => (
            <span
              key={index}
              className="absolute inset-y-0.5 flex items-center overflow-hidden rounded-[4px] border px-1 text-[0.62rem] font-bold whitespace-nowrap"
              style={{
                ...LANE_TONES[laneBlock.veille ? "veille" : laneBlock.kind],
                left: `${laneBlock.x0}%`,
                width: `calc(${Math.max(laneBlock.laneX1 - laneBlock.x0, 0.6)}% - 2px)`,
                boxShadow: atStop && index === frame.block ? "0 0 0 2px #0f172a" : undefined,
                zIndex: atStop && index === frame.block ? 1 : undefined,
              }}
              title={`${laneBlock.label} · ${laneBlock.kind === "load" ? "chargement" : "livraison"} ${formatTons(laneBlock.tons)}`}
            >
              <span className="truncate">{laneBlock.veille ? "PM" : laneBlock.label}</span>
            </span>
          ))}
          <span className="pointer-events-none absolute inset-y-0 left-0 z-[2] rounded-l-[4px] bg-slate-900/15" style={{ width: `${frame.x}%` }} />
          <span className="pointer-events-none absolute -bottom-1.5 -top-1.5 z-[3] w-0.5 -translate-x-1/2 bg-slate-900" style={{ left: `${frame.x}%` }}>
            <span className="absolute -left-[5px] -top-1 h-3 w-3 rounded-none bg-slate-900" />
          </span>
          <span
            className="pointer-events-none absolute bottom-[calc(100%+10px)] z-[3] -translate-x-1/2 whitespace-nowrap rounded-[4px] bg-slate-900 px-1.5 text-[0.66rem] font-extrabold leading-4 tabular-nums text-white"
            style={{ left: `clamp(48px, ${frame.x}%, calc(100% - 48px))` }}
            data-truck-load-clock
          >
            {minutes == null ? "Veille · Chargé PM" : formatClock(minutes)}
          </span>
        </div>
        <div className="relative mt-1 h-4 text-[0.62rem] tabular-nums text-slate-500">
          {timeline.veilleShare ? <span className="absolute -translate-x-1/2 font-bold text-[#7a5b00]" style={{ left: `${timeline.veilleShare / 2}%` }}>Veille</span> : null}
          {timeline.hourTicks.map((tick) => (
            <span key={tick.minutes} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${tick.x}%` }}>{formatClock(tick.minutes)}</span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={`${SECONDARY_BUTTON_CLASS_NAME} h-7 px-2 text-[0.75rem]`} onClick={backToPreviousStop} title="Arrêt précédent (←)">
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />Arrêt
        </button>
        <button
          type="button"
          className="inline-flex h-7 min-w-24 items-center justify-center gap-1.5 rounded-[4px] border border-accent bg-accent px-2.5 text-[0.75rem] font-bold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1"
          onClick={togglePlay}
          aria-pressed={playing}
        >
          {playing ? <Pause className="h-3.5 w-3.5" aria-hidden="true" /> : <Play className="h-3.5 w-3.5" aria-hidden="true" />}
          {playing ? "Pause" : "Lecture"}
        </button>
        <button type="button" className={`${SECONDARY_BUTTON_CLASS_NAME} h-7 px-2 text-[0.75rem]`} onClick={playNextStop} title="Jouer l'arrêt suivant (→)">
          Arrêt<ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <span className="inline-flex flex-wrap gap-1">
          {products.map((product) => (
            <span key={product} className={`${CHIP_CLASS_NAME} bg-slate-100 text-slate-800`}>
              <i className="h-2.5 w-2.5 rounded-none" style={{ background: getFleetProductTone(product).accentColor }} />{product}
            </span>
          ))}
          <span className={`${CHIP_CLASS_NAME} text-white`} style={{ background: LOADING_COLOR }}>Chargé ici</span>
          <span className={`${CHIP_CLASS_NAME} text-[#1f1606]`} style={{ background: DELIVERY_COLOR }}>Livré ici</span>
        </span>
        <span className="ml-auto text-[0.68rem] text-slate-500">Glisser sur la ligne du temps · Espace · ← →</span>
      </div>
    </div>
  );
}
